import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Bot, Star } from "lucide-react";
import React from "react";
import { useGetAiMarketplaceListingsQuery } from "@/store/rest_api";
import { AI_AGENT_CATEGORIES } from "@/model/interface/AiAgent";
import { listingListPriceLabel, listingMonthlyLabel, listingPerMinuteLabel } from "@/util/aiListingPricing";

export default function AiMarketplacePage() {
  const navigate = useNavigate();
  const [category, setCategory] = useState<string>("");
  const { data: listingsPage, isLoading } = useGetAiMarketplaceListingsQuery({
    category: category || undefined,
    size: 50,
  });

  const listings = listingsPage?.items ?? [];

  return (
    <div className="space-y-6 p-4">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/ai-agents")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">AI Agent Marketplace</h1>
          <p className="text-muted-foreground">Browse and acquire AI agents to handle your inbound calls</p>
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {AI_AGENT_CATEGORIES.map((category) => (
              <SelectItem key={category.value} value={category.value}>{category.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64 text-muted-foreground">Loading marketplace...</div>
      ) : listings.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Bot className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No agents available</h3>
            <p className="text-muted-foreground text-center max-w-md">
              {category ? "No agents found in this category. Try a different filter." : "No agents are available in the marketplace yet. Check back later."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {listings.map((listing) => (
            <Card
              key={listing.id}
              className="flex flex-col cursor-pointer"
              onClick={() => navigate(`/ai-marketplace/${listing.id}`)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {listing.template?.iconUrl ? (
                      <img src={listing.template.iconUrl} alt="" className="h-10 w-10 rounded-lg object-cover" />
                    ) : (
                      <div className="p-2 rounded-lg bg-cyan-500 text-white">
                        <Bot className="h-6 w-6" />
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-lg">{listing.displayName || listing.template?.name}</CardTitle>
                      <div className="mt-1 flex flex-wrap gap-1">
                        <Badge variant="outline">
                          {AI_AGENT_CATEGORIES.find((cat) => cat.value === listing.template?.category)?.label || listing.template?.category}
                        </Badge>
                        {listing.visibility === "PRIVATE" && <Badge>Private offer</Badge>}
                      </div>
                    </div>
                  </div>
                  {listing.featured && (
                    <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1">
                <p className="text-sm text-muted-foreground">
                  {listing.longDescription || "No description available."}
                </p>
                <div className="flex gap-4 mt-4 text-sm">
                  <div>
                    <div className="text-muted-foreground">Monthly</div>
                    <div className="font-semibold">{listingMonthlyLabel(listing)}</div>
                    {listingListPriceLabel(listing) && (
                      <div className="text-xs text-muted-foreground line-through">{listingListPriceLabel(listing)}</div>
                    )}
                  </div>
                  <div>
                    <div className="text-muted-foreground">Per minute</div>
                    <div className="font-semibold">{listingPerMinuteLabel(listing)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Users</div>
                    <div className="font-semibold">{listing.acquisitionCount}</div>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button className="w-full" onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/ai-marketplace/${listing.id}`);
                }}>
                  View Agent
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
